import React, { useState } from "react";
import { formatCompactCurrency, formatCurrency } from "@/utils/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Shield, TrendingUp, IndianRupee, CheckCircle, Clock } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useEmergencyFundCalculator } from "@/hooks/useCalculatorApi";

interface EmergencyFundInputs {
  monthlyExpenses: number;
  jobStability: "high" | "medium" | "low";
  dependents: number;
  hasInsurance: boolean;
  currentSavings: number;
  riskFactors: string[];
  incomeType: "salaried" | "business" | "freelance";
}

interface Recommendation {
  type: "critical" | "important" | "success" | "tip" | "suggestion";
  title: string;
  description: string;
}

interface EmergencyFundResult {
  summary: {
    requiredAmount: number;
    currentAmount: number;
    shortfall: number;
    surplus: number;
    totalMonths: number;
    adequacyRatio: string;
  };
  breakdown: {
    baseMonths: number;
    adjustments: {
      jobStability: number;
      incomeType: number;
      riskFactors: number;
      dependents: number;
      insurance: number;
    };
  };
  savingPlans: {
    sixMonths: number;
    twelveMonths: number;
    twentyFourMonths: number;
  };
  recommendations: Recommendation[];
}

interface GenerateRecommendationsParams {
  totalMonths: number;
  adequacyRatio: number;
  shortfall: number;
  inputs: EmergencyFundInputs;
}

/**
 * Emergency Fund Calculator component for determining optimal emergency corpus.
 * Calculates fund requirements based on expenses, stability, and risk factors.
 * @example
 * <EmergencyFundCalculator />
 * @returns {JSX.Element} Complete emergency fund planning interface.
 */
const EmergencyFundCalculator: React.FC = () => {
  const [inputs, setInputs] = useState<EmergencyFundInputs>({
    monthlyExpenses: 50000,
    jobStability: "medium",
    dependents: 2,
    hasInsurance: false,
    currentSavings: 200000,
    riskFactors: [],
    incomeType: "salaried",
  });

  const [result, setResult] = useState<EmergencyFundResult | null>(null);
  const { loading, calculateEmergencyFund } = useEmergencyFundCalculator();

  const riskFactorOptions = [
    { id: "single_income", label: "Single income household" },
    { id: "health_issues", label: "Family health issues" },
    { id: "aging_parents", label: "Aging parents to support" },
    { id: "unstable_industry", label: "Unstable industry/sector" },
    { id: "high_debt", label: "High debt obligations" },
    { id: "no_investments", label: "No liquid investments" },
  ];

  const handleCalculateEmergencyFund = async () => {
    const apiResult = await calculateEmergencyFund(inputs);
    if (!apiResult) {
      return;
    }

    const adequacyRatio = Number.parseFloat(apiResult.summary.adequacyRatio);

    const recommendations = generateRecommendations({
      totalMonths: apiResult.summary.totalMonths,
      adequacyRatio,
      shortfall: apiResult.summary.shortfall,
      inputs,
    });

    setResult({
      ...apiResult,
      recommendations,
    });
  };

  const generateRecommendations = ({
    totalMonths,
    adequacyRatio,
    shortfall,
    inputs,
  }: GenerateRecommendationsParams): Recommendation[] => {
    const recommendations = [];

    if (adequacyRatio < 50) {
      recommendations.push({
        type: "critical",
        title: "Build Emergency Fund Immediately",
        description: `Your current fund covers only ${adequacyRatio}% of needs. Start building emergency fund as top priority.`,
      });
    } else if (adequacyRatio < 80) {
      recommendations.push({
        type: "important",
        title: "Increase Emergency Fund",
        description: `You need ${formatCompactCurrency(shortfall)} more to reach optimal emergency fund size.`,
      });
    } else {
      recommendations.push({
        type: "success",
        title: "Well Prepared!",
        description: "Your emergency fund is adequate. Consider investing surplus in liquid funds.",
      });
    }

    if (totalMonths > 9) {
      recommendations.push({
        type: "tip",
        title: "Consider Risk Reduction",
        description:
          "Your high-risk profile suggests a large emergency fund. Consider life/health insurance to reduce requirements.",
      });
    }

    if (!inputs.hasInsurance) {
      recommendations.push({
        type: "suggestion",
        title: "Get Adequate Insurance",
        description:
          "Health and life insurance can significantly reduce your emergency fund requirements.",
      });
    }

    recommendations.push({
      type: "tip",
      title: "Keep it Liquid",
      description:
        "Park emergency funds in savings account, liquid funds, or short-term FDs for easy access.",
    });

    return recommendations;
  };

  const handleRiskFactorChange = (factorId: string, checked: boolean) => {
    setInputs((prev) => ({
      ...prev,
      riskFactors: checked
        ? [...prev.riskFactors, factorId]
        : prev.riskFactors.filter((id) => id !== factorId),
    }));
  };

  const adequacyRatio = result ? Number.parseFloat(result.summary.adequacyRatio) : 0;
  const isAdequate = adequacyRatio >= 80;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">Emergency Fund Calculator</h2>
        </div>
        <p className="text-muted-foreground">
          Calculate the ideal emergency fund size based on your risk profile
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Your Financial Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Monthly Expenses</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  className="pl-10"
                  value={inputs.monthlyExpenses}
                  onChange={(e) =>
                    setInputs((prev) => ({
                      ...prev,
                      monthlyExpenses: Number.parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  placeholder="Monthly expenses"
                />
              </div>
            </div>

            <div>
              <Label>Job Stability</Label>
              <Select
                value={inputs.jobStability}
                onValueChange={(value: "high" | "medium" | "low") =>
                  setInputs((prev) => ({
                    ...prev,
                    jobStability: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High (Government, PSU, MNC)</SelectItem>
                  <SelectItem value="medium">Medium (Stable private job)</SelectItem>
                  <SelectItem value="low">Low (Startup, contract work)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Income Type</Label>
              <Select
                value={inputs.incomeType}
                onValueChange={(value: "salaried" | "business" | "freelance") =>
                  setInputs((prev) => ({
                    ...prev,
                    incomeType: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salaried">Salaried Employee</SelectItem>
                  <SelectItem value="business">Business Owner</SelectItem>
                  <SelectItem value="freelance">Freelancer/Consultant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Number of Dependents</Label>
              <Input
                type="number"
                value={inputs.dependents}
                onChange={(e) =>
                  setInputs((prev) => ({
                    ...prev,
                    dependents: Number.parseInt(e.target.value, 10) || 0,
                  }))
                }
                min={0}
                max={10}
              />
            </div>

            <div>
              <Label>Current Emergency Savings</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  className="pl-10"
                  value={inputs.currentSavings}
                  onChange={(e) =>
                    setInputs((prev) => ({
                      ...prev,
                      currentSavings: Number.parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  placeholder="Current savings"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="insurance"
                checked={inputs.hasInsurance}
                onCheckedChange={(checked) =>
                  setInputs((prev) => ({
                    ...prev,
                    hasInsurance: !!checked,
                  }))
                }
              />
              <Label htmlFor="insurance">I have adequate health & life insurance</Label>
            </div>

            <div>
              <Label>Risk Factors (Select all that apply)</Label>
              <div className="space-y-2 mt-2">
                {riskFactorOptions.map((option) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={option.id}
                      checked={inputs.riskFactors.includes(option.id)}
                      onCheckedChange={(checked) => handleRiskFactorChange(option.id, !!checked)}
                    />
                    <Label htmlFor={option.id} className="text-sm">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleCalculateEmergencyFund} className="w-full" disabled={loading}>
              {loading ? "Calculating..." : "Calculate Emergency Fund"}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield
                  className={`h-5 w-5 ${isAdequate ? "text-green-600" : "text-orange-600"}`}
                />
                Emergency Fund Analysis
                <Badge variant={isAdequate ? "default" : "secondary"} className="ml-auto">
                  {adequacyRatio}% Ready
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Adequacy Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Fund Adequacy</span>
                  <span>{adequacyRatio}%</span>
                </div>
                <Progress
                  value={Math.min(adequacyRatio, 100)}
                  aria-label={`Fund adequacy: ${adequacyRatio}%`}
                  className={`h-3 ${adequacyRatio >= 80 ? "bg-green-100" : "bg-orange-100"}`}
                />
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Required Fund</p>
                  <p className="text-lg font-bold text-primary">
                    {formatCompactCurrency(result.summary.requiredAmount)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {result.summary.totalMonths} months
                  </p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Current Savings</p>
                  <p className="text-lg font-bold">
                    {formatCompactCurrency(result.summary.currentAmount)}
                  </p>
                </div>
              </div>

              {result.summary.shortfall > 0 ? (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-semibold">
                        Shortfall: {formatCompactCurrency(result.summary.shortfall)}
                      </p>
                      <p className="text-sm">You need to build additional emergency savings</p>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-semibold">Well Prepared!</p>
                      <p className="text-sm">Your emergency fund is adequate</p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Saving Plans */}
              {result.summary.shortfall > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Saving Plans
                  </h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-muted/20 rounded">
                      <p className="text-xs text-muted-foreground">6 Months</p>
                      <p className="font-medium">
                        {formatCurrency(result.savingPlans.sixMonths)}/mo
                      </p>
                    </div>
                    <div className="text-center p-2 bg-muted/20 rounded">
                      <p className="text-xs text-muted-foreground">12 Months</p>
                      <p className="font-medium">
                        {formatCurrency(result.savingPlans.twelveMonths)}/mo
                      </p>
                    </div>
                    <div className="text-center p-2 bg-muted/20 rounded">
                      <p className="text-xs text-muted-foreground">24 Months</p>
                      <p className="font-medium">
                        {formatCurrency(result.savingPlans.twentyFourMonths)}/mo
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Fund Breakdown */}
              <div className="space-y-2">
                <h4 className="font-semibold">Calculation Breakdown</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Base months:</span>
                    <span>{result.breakdown.baseMonths}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Job stability factor:</span>
                    <span>{result.breakdown.adjustments.jobStability}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Income type factor:</span>
                    <span>{result.breakdown.adjustments.incomeType}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Risk factors:</span>
                    <span>{result.breakdown.adjustments.riskFactors.toFixed(2)}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Dependents factor:</span>
                    <span>{result.breakdown.adjustments.dependents.toFixed(2)}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Insurance factor:</span>
                    <span>{result.breakdown.adjustments.insurance}x</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recommendations */}
      {result && result.recommendations.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.recommendations.map((rec: Recommendation) => (
            <Alert key={rec.title}>
              {(() => {
                if (rec.type === "critical") return <AlertTriangle className="h-4 w-4" />;
                if (rec.type === "success") return <CheckCircle className="h-4 w-4" />;
                return <TrendingUp className="h-4 w-4" />;
              })()}
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-semibold">{rec.title}</p>
                  <p className="text-sm">{rec.description}</p>
                </div>
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmergencyFundCalculator;
